import { useState, useEffect, useCallback, useRef } from "react";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useIsMobile from "../hooks/useIsMobile";
import api from "../api/client";
import useAuth from "../hooks/useAuth";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateHeader(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function SortIcon({ direction }) {
  if (!direction) {
    return (
      <svg className="w-3 h-3 ml-1 inline opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return direction === "asc" ? (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function DailyOutputReportPage({
  title,
  label,
  endpoint,
  detailEndpoint,
  detailParamName,
  exportEndpoint,
  filterEndpoint,
  filterLabel,
  filterParamName,
  exportFilenamePrefix,
  nameExtractor,
  showCompanyFilter = false,
}) {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Company filter
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.company_id ?? "");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detailModal, setDetailModal] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch companies for dropdown
  useEffect(() => {
    if (showCompanyFilter) {
      api.get("/reports/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    }
  }, [showCompanyFilter]);

  // Fetch filter options (teams/personnel) — re-fetch when company changes
  useEffect(() => {
    if (filterEndpoint) {
      const params = {};
      if (showCompanyFilter && selectedCompanyId) {
        params.company_id = selectedCompanyId;
      }
      api.get(filterEndpoint, { params }).then(({ data }) => {
        setFilterOptions(data);
        setSelectedFilters([]);
      }).catch(() => {});
    }
  }, [filterEndpoint, showCompanyFilter, selectedCompanyId]);

  const _companyParam = useCallback(() => {
    if (showCompanyFilter && selectedCompanyId) {
      return { company_id: selectedCompanyId };
    }
    return {};
  }, [showCompanyFilter, selectedCompanyId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo, ..._companyParam() };
    if (selectedFilters.length > 0) {
      params[filterParamName] = selectedFilters.join(",");
    }
    try {
      const { data } = await api.get(endpoint, { params });
      setReportData(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedFilters, endpoint, filterParamName, _companyParam]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleToday = () => {
    const t = today();
    setDateFrom(t);
    setDateTo(t);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, ..._companyParam() };
      if (selectedFilters.length > 0) {
        params[filterParamName] = selectedFilters.join(",");
      }
      const { data } = await api.get(exportEndpoint, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportFilenamePrefix}_${dateFrom}_${dateTo}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // handled by interceptor
    } finally {
      setExporting(false);
    }
  };

  const handleCellClick = async (rowName, dateIso) => {
    setDetailModal({ rowName, dateIso });
    setDetailLoading(true);
    setDetailData(null);
    try {
      const paramValue = nameExtractor ? nameExtractor(rowName) : rowName;
      const { data } = await api.get(detailEndpoint, {
        params: { [detailParamName]: paramValue, job_date: dateIso, ..._companyParam() },
      });
      setDetailData(data);
    } catch {
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleFilter = (name) => {
    setSelectedFilters((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name]
    );
  };

  const [sortKey, setSortKey] = useState("total");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortedRows = reportData
    ? [...reportData.rows].sort((a, b) => {
        if (!sortKey) return 0;
        let va, vb;
        if (sortKey === "name") {
          va = (a.name || "").toLowerCase();
          vb = (b.name || "").toLowerCase();
          return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        if (sortKey === "total") {
          va = a.total;
          vb = b.total;
        } else {
          va = a.counts[sortKey] || 0;
          vb = b.counts[sortKey] || 0;
        }
        return sortDir === "asc" ? va - vb : vb - va;
      })
    : [];

  const thClass =
    "px-3 py-2 font-semibold text-gray-700 min-w-[80px] cursor-pointer select-none hover:bg-gray-200 transition-colors";

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        <div className="flex items-center gap-3 mb-4">
          <a href="/reports" className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Reports
          </a>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">{title}</h1>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            />
          </div>

          <button
            onClick={handleToday}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded border transition-colors"
          >
            Today
          </button>

          {/* Company filter */}
          {showCompanyFilter && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value ? Number(e.target.value) : "")}
                className="border rounded px-3 py-1.5 text-sm bg-white min-w-[180px]"
              >
                <option value="">All Companies</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Filter multi-select */}
          {filterEndpoint && (
            <div className="relative" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{filterLabel}</label>
              <button
                onClick={() => setDropdownOpen((o) => !o)}
                className="border rounded px-3 py-1.5 text-sm bg-white min-w-[220px] text-left flex items-center justify-between"
              >
                <span className="truncate">
                  {selectedFilters.length === 0
                    ? `All ${filterLabel}s`
                    : selectedFilters.length === filterOptions.length
                    ? `All ${filterLabel}s`
                    : `${selectedFilters.length} selected`}
                </span>
                <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-72 bg-white border rounded shadow-lg max-h-64 overflow-auto">
                  <div className="flex gap-2 px-3 py-2 border-b sticky top-0 bg-white">
                    <button onClick={() => setSelectedFilters([...filterOptions])} className="text-xs text-blue-600 hover:underline">Select All</button>
                    <button onClick={() => setSelectedFilters([])} className="text-xs text-blue-600 hover:underline">Clear All</button>
                  </div>
                  {filterOptions.map((name) => (
                    <label key={name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedFilters.includes(name)}
                        onChange={() => toggleFilter(name)}
                        className="rounded"
                      />
                      <span className="truncate">{name}</span>
                    </label>
                  ))}
                  {filterOptions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-400">No {filterLabel.toLowerCase()}s found</div>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleExport}
            disabled={exporting || !reportData || reportData.rows.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>

        {/* Table */}
        {loading && (
          <div className="text-center text-gray-500 py-8">Loading report...</div>
        )}

        {!loading && reportData && sortedRows.length === 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-8 text-center text-gray-400">No data for the selected filters.</div>
          </div>
        )}

        {!loading && reportData && sortedRows.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th
                      onClick={() => handleSort("name")}
                      className="px-3 py-2 text-left font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10 min-w-[200px] cursor-pointer select-none hover:bg-gray-200 transition-colors"
                    >
                      {label}
                      <SortIcon direction={sortKey === "name" ? sortDir : null} />
                    </th>
                    {reportData.dates.map((d) => (
                      <th
                        key={d}
                        onClick={() => handleSort(d)}
                        className={`${thClass} text-center`}
                      >
                        {formatDateHeader(d)}
                        <SortIcon direction={sortKey === d ? sortDir : null} />
                      </th>
                    ))}
                    <th
                      onClick={() => handleSort("total")}
                      className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50 min-w-[80px] cursor-pointer select-none hover:bg-blue-100 transition-colors"
                    >
                      Total
                      <SortIcon direction={sortKey === "total" ? sortDir : null} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, idx) => (
                    <tr key={row.name} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td
                        className="px-3 py-2 font-medium text-gray-800 sticky left-0 z-10"
                        style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb" }}
                      >
                        {row.name}
                      </td>
                      {reportData.dates.map((d) => {
                        const count = row.counts[d] || 0;
                        return (
                          <td key={d} className="px-3 py-2 text-center text-gray-700">
                            {count > 0 ? (
                              <button
                                onClick={() => handleCellClick(row.name, d)}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              >
                                {count}
                              </button>
                            ) : (
                              0
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center font-bold text-gray-800 bg-blue-50">
                        {row.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 font-bold">
                    <td className="px-3 py-2 text-gray-800 sticky left-0 bg-blue-50 z-10">Grand Total</td>
                    {reportData.dates.map((d) => (
                      <td key={d} className="px-3 py-2 text-center text-gray-800">
                        {reportData.grand_totals[d] || 0}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-gray-900 bg-blue-100">
                      {reportData.grand_totals.total || 0}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">
                {detailModal.rowName} — {formatDateHeader(detailModal.dateIso)}
              </h3>
              <button
                onClick={() => setDetailModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto flex-1 px-6 py-4">
              {detailLoading && <div className="text-center text-gray-500 py-4">Loading...</div>}
              {!detailLoading && detailData && detailData.length === 0 && (
                <div className="text-center text-gray-400 py-4">No records found.</div>
              )}
              {!detailLoading && detailData && detailData.length > 0 && (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">JO Number</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Account #</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Customer</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Municipality</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Plan</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Team</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.map((jo, i) => (
                      <tr key={jo.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-3 py-2 text-gray-800">{jo.jo_number || "—"}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.account_number}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.customer_full_name}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.municipality}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.plan || "—"}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.team_number || "—"}</td>
                        <td className="px-3 py-2 text-gray-700">{jo.jo_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-3 border-t text-right text-sm text-gray-500">
              {detailData ? `${detailData.length} record(s)` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
